import { useMemo } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Wrench, DollarSign, MessageSquare, Settings } from "lucide-react";
import { usePropertyManagement } from "@/hooks/use-property-management";
import { usePortfolio } from "@/hooks/use-portfolio";
import { usePropertyMeta } from "@/hooks/use-property-meta";
import { WorkOrdersTab } from "@/components/property-management/WorkOrdersTab";
import { PaymentsTab } from "@/components/property-management/PaymentsTab";
import { TenantChatTab } from "@/components/property-management/TenantChatTab";
import { PMSettingsTab } from "@/components/property-management/PMSettingsTab";

export default function PropertyManagement() {
  const {
    workOrders,
    loadingWorkOrders,
    createWorkOrder,
    updateWorkOrder,
    deleteWorkOrder,
    rentPayments,
    loadingPayments,
    createPayment,
    updatePayment,
    deletePayment,
    messages,
    loadingMessages,
    sendMessage,
    pmSettings,
    loadingSettings,
    upsertSettings,
  } = usePropertyManagement();

  const { portfolio } = usePortfolio();

  const portfolioProperties = useMemo(
    () =>
      portfolio.map((p) => ({
        id: p.id,
        label: p.property?.address || `Property ${p.id.slice(0, 6)}`,
      })),
    [portfolio],
  );

  // Covers every pipeline property, not just the portfolio list — work orders
  // and payments outlive a property's stay on /properties (active rehab drops
  // it from `portfolio`, but its records keep pointing at it).
  const propertyMeta = usePropertyMeta();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">
          Property Management
        </h1>
        <p className="text-muted-foreground mt-1">
          Work orders, payments, tenant communication & settings
        </p>
      </div>

      <Tabs defaultValue="work-orders" className="space-y-4">
        <TabsList>
          <TabsTrigger value="work-orders" className="gap-2">
            <Wrench className="h-4 w-4" /> Work Orders
          </TabsTrigger>
          <TabsTrigger value="payments" className="gap-2">
            <DollarSign className="h-4 w-4" /> Payments
          </TabsTrigger>
          <TabsTrigger value="messages" className="gap-2">
            <MessageSquare className="h-4 w-4" /> Tenant Chat
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" /> Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="work-orders">
          <WorkOrdersTab
            workOrders={workOrders}
            isLoading={loadingWorkOrders}
            portfolioProperties={portfolioProperties}
            propertyMeta={propertyMeta}
            onCreate={(wo) => createWorkOrder.mutate(wo)}
            onUpdate={(wo) => updateWorkOrder.mutate(wo)}
            onDelete={(id, opts) => deleteWorkOrder.mutate(id, opts)}
            isSaving={createWorkOrder.isPending}
            isUpdating={updateWorkOrder.isPending}
            isDeleting={deleteWorkOrder.isPending}
          />
        </TabsContent>

        <TabsContent value="payments">
          <PaymentsTab
            payments={rentPayments}
            isLoading={loadingPayments}
            portfolioProperties={portfolioProperties}
            propertyMeta={propertyMeta}
            onCreate={(p) => createPayment.mutate(p)}
            onUpdate={(p) => updatePayment.mutate(p)}
            onDelete={(id, opts) => deletePayment.mutate(id, opts)}
            isSaving={createPayment.isPending}
            isUpdating={updatePayment.isPending}
            isDeleting={deletePayment.isPending}
          />
        </TabsContent>

        <TabsContent value="messages">
          <TenantChatTab
            messages={messages}
            isLoading={loadingMessages}
            portfolioProperties={portfolioProperties}
            onSend={(msg) => sendMessage.mutate(msg)}
          />
        </TabsContent>

        <TabsContent value="settings">
          <PMSettingsTab
            settings={pmSettings}
            portfolioProperties={portfolioProperties}
            onSave={(s) => upsertSettings.mutate(s)}
            isSaving={upsertSettings.isPending}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
